import React from "react";
import { Box } from "components/Box";
import {
  useBettingEvents,
  type Event as EventType,
} from "hooks/firestore/betting/useBetting";
import { defaultPageSize } from "utilities/display";
import { useBettingFilters } from "../hooks/useBettingFilters";
import { LoadMore } from "components/LoadMore";
import { Link } from "components/Link";
import { ReactComponent as EnclosedPromoIcon } from "components/assets/icon-promo-inside.svg";
import { ReactComponent as EmptyIcon } from "sections/Entries/assets/empty/upcoming.svg";
import { TitleIcon } from "components/TitleIcon";
import { useTitle } from "hooks/graphql/useTitles";
import { Clamp } from "components/Clamp";
import Icon from "library/components/Icon";
import { Skeleton } from "components/Skeleton";
import { PageMessage } from "components/PageMessage";
import { BettingEmptyStateActions } from "./BettingEmptyStateActions";
import type { Hub } from "hooks/graphql/useHubs";
import useRestoreScrollPosition from "hooks/useRestoreScrollPosition";

import * as styles from "./OutrightsList.module.scss";

type OutrightsListProps = {
  page: number;
  setPage: (newPage: number) => void;
  sportsTitles: string[];
  canSGMBet: boolean;
  hub: Hub;
};

const OutrightRowSkeleton = () => {
  return (
    <Skeleton className={styles.outrightRowSkeleton}>
      <Skeleton shape="circle" className={styles.iconSkeleton} mode="light" />

      <div className={styles.headerSkeleton}>
        <Skeleton mode="light" className={styles.titleSkeleton} />
        <Skeleton mode="light" className={styles.subtitleSkeleton} />
      </div>

      <Skeleton className={styles.clampSkeleton} />
    </Skeleton>
  );
};

const OutrightRow = ({ outright }: { outright: EventType }) => {
  const title = useTitle(outright.sport?.toLowerCase());

  if (!outright || outright.eventType === "MATCH") return null;

  return (
    <Link
      to={`${outright.hub === "sports" ? "/sports" : ""}/betting/outright/${
        outright.eventId
      }`}
      className={styles.outrightRow}
    >
      <div className={styles.icon}>
        <TitleIcon title={title} svg className={styles.titleIcon} />
        {outright.promotionIsAvailable && (
          <div className={styles.promoIcon}>
            <EnclosedPromoIcon />
          </div>
        )}
      </div>

      <div className={styles.header}>
        <div className={styles.title}>{outright.eventName}</div>
        <div className={styles.subtitle}>{title.name}</div>
      </div>

      <Clamp className={styles.clamp} variant="grey">
        {outright.activeMarketCount}
        <Icon type="arrow-right" className={styles.arrowIcon} inline />
      </Clamp>
    </Link>
  );
};

const OutrightsListSkeleton = () => {
  return (
    <div>
      {Array.from({ length: 12 }).map((_, index) => (
        <OutrightRowSkeleton key={`outright-row-skeleton-${index}`} />
      ))}
    </div>
  );
};

export const OutrightsList = ({
  page,
  setPage,
  sportsTitles,
  canSGMBet,
  hub,
}: OutrightsListProps) => {
  const [filters, setFilters] = useBettingFilters();

  const [events, eventsLoading] = useBettingEvents({
    page,
    titles: sportsTitles,
    tournaments: filters.tour,
    tab: filters.tab,
    hub: hub.name,
    sgm: canSGMBet && filters.sgm === "true",
  });

  useRestoreScrollPosition(eventsLoading);

  if (eventsLoading && page == 1) {
    return <OutrightsListSkeleton />;
  }

  if (events.length === 0 && !eventsLoading) {
    return (
      <PageMessage
        title={"No events found"}
        subTitle={`There are currently no Outright markets which match your filters`}
        image={<EmptyIcon />}
        className={styles.pageContainer}
      >
        <BettingEmptyStateActions />
      </PageMessage>
    );
  }

  return (
    <div>
      <Box className={styles.outrightsList} hideHeader>
        {events.map((event) => (
          <OutrightRow key={event.eventId} outright={event} />
        ))}
      </Box>

      <LoadMore
        hasMore={
          !eventsLoading ? page * defaultPageSize() <= events.length : true
        }
        next={() => {
          if (!eventsLoading) {
            setPage(Number(page) + 1);
            setFilters({ page: Number(page) + 1 });
          }
        }}
      />
    </div>
  );
};
